import { useState, useEffect, useCallback } from 'react';
import { Content, Header, Page, HeaderLabel, InfoCard } from '@backstage/core-components';
import {
  CircularProgress,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Button,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useApi } from '@backstage/core-plugin-api';
import { githubWorkflowApiRef } from '../plugin';
import { WorkflowRunInfo, WorkflowJobInfo } from '../types';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  jobCard: {
    marginBottom: theme.spacing(2),
  },
  stepList: {
    paddingLeft: theme.spacing(2),
  },
  stepItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  refreshButton: {
    marginLeft: theme.spacing(1),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  backButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
}));

export interface WorkflowRunStatusPageProps {
  owner: string;
  repo: string;
  runId: number;
  workflowName?: string;
  onBack?: () => void;
}

const getStatusIcon = (status: string | null, conclusion: string | null) => {
  if (status === 'completed') {
    if (conclusion === 'success') return '✅';
    if (conclusion === 'failure') return '❌';
    if (conclusion === 'cancelled') return '🚫';
    if (conclusion === 'skipped') return '⏭️';
  }
  if (status === 'in_progress') return '🔄';
  if (status === 'queued') return '⏳';
  return '⚪';
};

const getStatusText = (status: string | null, conclusion: string | null) => {
  if (status === 'completed') {
    return conclusion || 'completed';
  }
  return status || 'unknown';
};

export const WorkflowRunStatusPage = ({ owner, repo, runId, workflowName, onBack }: WorkflowRunStatusPageProps) => {
  const classes = useStyles();
  const api = useApi(githubWorkflowApiRef);
  const [workflowRun, setWorkflowRun] = useState<WorkflowRunInfo | null>(null);
  const [jobs, setJobs] = useState<WorkflowJobInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const fetchData = useCallback(
    async (showRefreshIndicator = false) => {
      try {
        if (showRefreshIndicator) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const [runData, jobsData] = await Promise.all([
          api.getWorkflowRun(owner, repo, runId),
          api.getWorkflowRunJobs(owner, repo, runId),
        ]);

        setWorkflowRun(runData);
        setJobs(jobsData.jobs);
        setError('');
      } catch (err) {
        setError(`Failed to fetch workflow data: ${err}`);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [api, owner, repo, runId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 10 seconds if workflow is still running
  useEffect(() => {
    if (!workflowRun || workflowRun.status === 'completed') {
      return undefined;
    }

    const interval = setInterval(() => {
      fetchData(true);
    }, 10000);

    return () => clearInterval(interval);
  }, [workflowRun, fetchData]);

  const handleRefresh = () => {
    fetchData(true);
  };

  const calculateProgress = (): number => {
    if (!jobs.length) return 0;
    const completedJobs = jobs.filter(job => job.status === 'completed').length;
    return (completedJobs / jobs.length) * 100;
  };

  const isRunning = workflowRun?.status === 'in_progress' || workflowRun?.status === 'queued';

  if (loading && !workflowRun) {
    return (
      <Page themeId="tool">
        <Header title="Loading Workflow Run..." subtitle="Fetching workflow run details" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Error" subtitle={error} />
        <Content>
          <Box p={3}>
            <Typography variant="body1">{error}</Typography>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title={
          <Box display="flex" alignItems="center">
            {workflowName || 'Workflow Run'}
            <span
              style={{ fontSize: '1.2em', marginLeft: 8 }}
              title={workflowRun ? getStatusText(workflowRun.status, workflowRun.conclusion).toUpperCase() : 'Unknown'}
            >
              {workflowRun ? getStatusIcon(workflowRun.status, workflowRun.conclusion) : '⚪'}
            </span>
            <IconButton onClick={handleRefresh} disabled={refreshing} className={classes.refreshButton} title="Refresh">
              <RefreshIcon />
            </IconButton>
            {workflowRun?.html_url && (
              <IconButton
                component="a"
                href={workflowRun.html_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', padding: 4 }}
                title="View on GitHub"
              >
                <LaunchIcon />
              </IconButton>
            )}
          </Box>
        }
      >
        {workflowRun?.created_at && (
          <HeaderLabel label="Started" value={new Date(workflowRun.created_at).toLocaleString()} />
        )}
      </Header>

      <Content>
        {refreshing && (
          <Box className={classes.progressContainer}>
            <Typography variant="body2" gutterBottom>
              Refreshing...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {isRunning && (
          <Box className={classes.progressContainer}>
            <Typography variant="body2" gutterBottom>
              Progress: {Math.round(calculateProgress())}% complete
            </Typography>
            <LinearProgress variant="determinate" value={calculateProgress()} />
          </Box>
        )}

        {jobs.map(job => (
          <InfoCard
            key={job.id}
            title={
              <Box display="flex" alignItems="center">
                {job.name}
                <span
                  style={{ fontSize: '1.1em', marginLeft: 8 }}
                  title={getStatusText(job.status, job.conclusion).toUpperCase()}
                >
                  {getStatusIcon(job.status, job.conclusion)}
                </span>
                {job.html_url && (
                  <IconButton
                    component="a"
                    href={job.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', padding: 4 }}
                    title="View job on GitHub"
                  >
                    <LaunchIcon />
                  </IconButton>
                )}
              </Box>
            }
            className={classes.jobCard}
          >
            {job.steps.length > 0 ? (
              <List dense className={classes.stepList}>
                {job.steps.map(step => (
                  <ListItem key={step.number} className={classes.stepItem}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          {step.name}
                          <span
                            style={{ fontSize: '1em', marginLeft: 8 }}
                            title={getStatusText(step.status, step.conclusion).toUpperCase()}
                          >
                            {getStatusIcon(step.status, step.conclusion)}
                          </span>
                        </Box>
                      }
                      secondary={
                        step.started_at && (
                          <Typography variant="caption" color="textSecondary">
                            {step.completed_at
                              ? `${new Date(step.started_at).toLocaleTimeString()} - ${new Date(step.completed_at).toLocaleTimeString()}`
                              : `Started: ${new Date(step.started_at).toLocaleTimeString()}`}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No steps information available
              </Typography>
            )}
          </InfoCard>
        ))}

        {jobs.length === 0 && !loading && (
          <InfoCard title="No Jobs Found">
            <Typography variant="body2" color="textSecondary">
              No jobs were found for this workflow run. The workflow may still be starting up.
            </Typography>
          </InfoCard>
        )}

        {onBack && (
          <Box className={classes.backButton}>
            <Button variant="contained" onClick={onBack} startIcon={<ArrowBackIcon />}>
              Back to Workflow Form
            </Button>
          </Box>
        )}
      </Content>
    </Page>
  );
};
